import { useEffect, useRef, useState } from "react";
import { call, register, remove } from "../../funcStore";
import { ATTRIBUTE_LABELS } from "../../data/mockVtubers";
import { FILTER_GROUPS } from "../../data/filterGroups";
import { SCREEN_DETAIL } from "../../App";

const styles = {
  tagList: {
    listStyle: "none",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "6px",
    padding: 0,
    margin: 0,
  },
  tagItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
  },
  tag: {
    background: "#eee",
    border: "none",
    borderRadius: "12px",
    padding: "2px 10px",
    fontSize: "0.85rem",
  },
  tagUnapproved: {
    opacity: 0.4,
  },
  tagRemoveButton: {
    background: "none",
    border: "none",
    color: "#a33",
    cursor: "pointer",
    fontSize: "0.8rem",
    lineHeight: 1,
    padding: "0 2px",
  },
  addTagButton: {
    background: "#fff",
    border: "1px dashed #999",
    borderRadius: "12px",
    padding: "2px 10px",
    fontSize: "0.85rem",
    lineHeight: 1.4,
    color: "#666",
    cursor: "pointer",
  },
  proposePanel: {
    flexBasis: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    padding: "10px",
    background: "#fafafa",
  },
  proposeGroups: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    maxHeight: "220px",
    overflowY: "auto",
  },
  proposeEmpty: {
    margin: 0,
    fontSize: "0.8rem",
    color: "#999",
  },
  proposeGroupTitle: {
    margin: "0 0 4px",
    fontSize: "0.85rem",
    fontWeight: "bold",
  },
  proposeFilterLabel: {
    margin: "4px 0 2px",
    fontSize: "0.8rem",
    color: "#666",
  },
  proposeOptionList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
  proposeOptionLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "0.8rem",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: "10px",
    padding: "2px 8px",
    cursor: "pointer",
  },
  proposeActions: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
  },
  proposeSubmitButton: {
    background: "#4b3f8f",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    padding: "2px 10px",
    fontSize: "0.8rem",
    cursor: "pointer",
  },
  proposeCancelButton: {
    background: "none",
    border: "none",
    color: "#999",
    fontSize: "0.8rem",
    cursor: "pointer",
    textDecoration: "underline",
  },
};

const STATE_BEFORE_INIT = "beforeInit";
const STATE_DETAIL_DISPLAY = "detailDisplay";
const STATE_CARD_DISPLAY = "cardDisplay";
const STATE_PROPOSING = "proposing";

function TagList({ parentKey }) {
  const [state, setState] = useState(STATE_BEFORE_INIT);

  const stateRef = useRef(state);
  stateRef.current = state;

  const funcKeyRef = useRef(`${parentKey}.tagList`);
  const funcKey = funcKeyRef.current;

  const dataRef = useRef({
    attributes: [],
    proposalGroups: [],
  });

  const proposeFormRef = useRef(null);

  const sync = () => {
    dataRef.current.attributes = call(`${parentKey}.getAttributes`)
  };

  const computeProposalGroups = () => {
    const existing = new Set(
      dataRef.current.attributes.map((attribute) => attribute.value),
    );

    const user_filter_group = FILTER_GROUPS.filter((group) => {
      return !["game", "streamStyle", "subscriber"].includes(group.key);
    });
    return user_filter_group
      .map((group) => ({
        key: group.key,
        title: group.title,
        filters: group.filters
          .map((filter) => ({
            key: filter.key,
            label: filter.label,
            options: filter.options.filter(
              (option) =>
                ATTRIBUTE_LABELS[option.value] && !existing.has(option.value),
            ),
          }))
          .filter((filter) => filter.options.length > 0),
      }))
      .filter((group) => group.filters.length > 0);
  };

  const init = () => {
    sync();

    register(`${funcKey}.getState`, () => stateRef.current);

    register(`${funcKey}.getAttributes`, () => dataRef.current.attributes);

    register(`${funcKey}.refresh`, () => {
      sync();
      setState(getKeyDisplay());
    });

    register(
      `${funcKey}.getProposalGroups`,
      () => dataRef.current.proposalGroups,
    );

    register(`${funcKey}.openPropose`, () => {
      if (stateRef.current !== getKeyDisplay()) {
        return;
      }

      dataRef.current.proposalGroups = computeProposalGroups();
      setState(STATE_PROPOSING);
    });

    register(`${funcKey}.closePropose`, () => {
      if (stateRef.current !== STATE_PROPOSING) {
        return;
      }

      setState(getKeyDisplay());
    });

    register(`${funcKey}.submitPropose`, (values) => {
      if (stateRef.current !== STATE_PROPOSING) {
        return;
      }

      if (!values || values.length === 0) {
        return;
      }

      call(`${parentKey}.proposeAttribute`, values);
      setState(getKeyDisplay());
    });

    setState(getKeyDisplay());
  };

  function getKeyDisplay() {
    const screenId = call("app.getState");

    return screenId == SCREEN_DETAIL
      ? STATE_DETAIL_DISPLAY
      : STATE_CARD_DISPLAY;
  }

  useEffect(() => {
    if (state === STATE_BEFORE_INIT) {
      init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    return () => {
      remove(`${funcKey}.getState`);
      remove(`${funcKey}.getAttributes`);
      remove(`${funcKey}.refresh`);
      remove(`${funcKey}.getProposalGroups`);
      remove(`${funcKey}.openPropose`);
      remove(`${funcKey}.closePropose`);
      remove(`${funcKey}.submitPropose`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state === STATE_BEFORE_INIT) {
    return null;
  }

  const handleTagClick = (value) => {
    call(`${parentKey}.tagClick`, value);
  };

  const handleRemoveTag = (value, approved) => {
    call(`${parentKey}.removeAttribute`, value, approved);
  };

  const isAdmin = call("app.isAdmin");

  const handleSubmitPropose = () => {
    const container = proposeFormRef.current;

    const values = container
      ? Array.from(
          container.querySelectorAll('input[type="checkbox"]:checked'),
        ).map((checkbox) => checkbox.value)
      : [];

    call(`${funcKey}.submitPropose`, values);
  };

  return (
    <ul style={styles.tagList}>
      {dataRef.current.attributes
        .filter((attribute) => ATTRIBUTE_LABELS[attribute.value])
        .map((attribute) => (
          <li key={attribute.value} style={styles.tagItem}>
            <button
              type="button"
              style={
                attribute.approved
                  ? styles.tag
                  : { ...styles.tag, ...styles.tagUnapproved }
              }
              onClick={() => handleTagClick(attribute.value)}
            >
              {ATTRIBUTE_LABELS[attribute.value]}
            </button>

            {isAdmin && (
              <button
                type="button"
                style={styles.tagRemoveButton}
                aria-label={`${ATTRIBUTE_LABELS[attribute.value]}を削除`}
                onClick={() => handleRemoveTag(attribute.value, attribute.approved)}
              >
                ×
              </button>
            )}
          </li>
        ))}

      {state === STATE_DETAIL_DISPLAY && (
        <li>
          <button
            type="button"
            style={styles.addTagButton}
            aria-label="属性を提案する"
            onClick={() => call(`${funcKey}.openPropose`)}
          >
            +
          </button>
        </li>
      )}

      {state === STATE_PROPOSING && (
        <li style={styles.proposePanel}>
          <div ref={proposeFormRef} style={styles.proposeGroups}>
            {dataRef.current.proposalGroups.length === 0 && (
              <p style={styles.proposeEmpty}>提案できる属性がありません</p>
            )}

            {dataRef.current.proposalGroups.map((group) => (
              <div key={group.key}>
                <p style={styles.proposeGroupTitle}>{group.title}</p>

                {group.filters.map((filter) => (
                  <div key={filter.key}>
                    <p style={styles.proposeFilterLabel}>{filter.label}</p>

                    <div style={styles.proposeOptionList}>
                      {filter.options.map((option) => (
                        <label
                          key={option.value}
                          style={styles.proposeOptionLabel}
                        >
                          <input type="checkbox" value={option.value} />
                          {option.label}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div style={styles.proposeActions}>
            <button
              type="button"
              style={styles.proposeSubmitButton}
              onClick={handleSubmitPropose}
            >
              提案する
            </button>

            <button
              type="button"
              style={styles.proposeCancelButton}
              onClick={() => call(`${funcKey}.closePropose`)}
            >
              キャンセル
            </button>
          </div>
        </li>
      )}
    </ul>
  );
}

export default TagList;
